import React, {
  createContext,
  useMemo,
  useContext,
  PropsWithChildren,
} from "react";
import { useGetRoles } from "@/api/pizza-checker/roles/roles";
import { useIdentity } from "@/context/identity-context";

export interface Role {
  _id: string;
  /** Missing on the original roles, which only have a description. */
  name?: string;
  permissions: string[];
  description: string;
  enabled: boolean;
}

export interface RolesData {
  roles: Role[];
}

interface RoleContextType {
  roles: Role[] | null;
  rolesLoading: boolean;
  getRoleById: (roleId: string) => Role | undefined;
  getRoleName: (roleId: string) => string;
  getAvailableRoles: () => Role[];
}

const RoleContext = createContext<RoleContextType | null>(null);

export const RoleProvider: React.FC<PropsWithChildren> = ({ children }) => {
  const { identity } = useIdentity();

  const { data, isLoading: rolesLoading } = useGetRoles<RolesData>({
    query: {
      enabled: !!identity?.token,
    },
  });

  const roles = useMemo(() => {
    return data?.roles || null;
  }, [data]);

  const getRoleById = (roleId: string): Role | undefined => {
    return roles?.find((role) => role._id === roleId);
  };

  const getRoleName = (roleId: string): string => {
    const role = getRoleById(roleId);
    return role?.name || role?.description || roleId;
  };

  const getAvailableRoles = (): Role[] => {
    if (!roles) return [];

    // The developer role is the one carrying the full "users" permission.
    // Only somebody who holds it may hand it out — the API refuses otherwise.
    const userRoleId = identity?.payload?.role_id;
    const userRole = userRoleId ? getRoleById(userRoleId) : null;
    const canAssignDeveloper = userRole?.permissions?.includes("users") ?? false;

    return roles.filter(role => {
      if (role.permissions?.includes("users") && !canAssignDeveloper) {
        return false;
      }
      return role.enabled !== false; // Show enabled roles
    });
  };

  return (
    <RoleContext.Provider
      value={useMemo(
        () => ({
          roles,
          rolesLoading,
          getRoleById,
          getRoleName,
          getAvailableRoles,
        }),
        [roles, rolesLoading],
      )}
    >
      {children}
    </RoleContext.Provider>
  );
};

export const useRole = () => {
  const context = useContext(RoleContext);
  if (!context) {
    throw new Error("useRole must be used within a RoleProvider");
  }
  return context;
};
